import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Droplets,
  Sparkles,
  Shield,
  Layers,
  Brush,
  Gauge,
  Check,
} from "lucide-react";
import { IMG } from "@/lib/images";

const benefits = [
  { icon: Droplets, title: "Fugenlos & flüssig verlegt", text: "Eine durchgehende Oberfläche ohne sichtbare Übergänge." },
  { icon: Sparkles, title: "Marmor- & Hochglanz-Optik", text: "Edler Spiegel-Look in jeder Wunschfarbe." },
  { icon: Shield,   title: "Robust & strapazierfähig",   text: "Beständig gegen Abrieb, Stöße und Chemikalien." },
  { icon: Layers,   title: "Sehr geringer Aufbau",       text: "Schon ab wenigen Millimetern Aufbauhöhe einsetzbar." },
  { icon: Brush,    title: "Individuell designt",        text: "Marmorierungen, Metallic-Effekte und Farben nach Wahl." },
  { icon: Gauge,    title: "Pflegeleicht",               text: "Glatte Oberfläche – schnell und einfach zu reinigen." },
];

const applications = [
  "Wohnräume & Lofts",
  "Eingangsbereiche & Foyers",
  "Praxen & Büros",
  "Showrooms & Verkaufsflächen",
  "Gastronomie",
  "Bäder & Wellnessbereiche",
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7 },
};

export default function PuDesignboden() {
  return (
    <div data-testid="pu-page">
      {/* HERO */}
      <section className="relative min-h-[78vh] flex items-end overflow-hidden grain">
        <img
          src={IMG.pu.hero}
          alt="PU Designboden mit Marmoroptik"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pb-20 pt-32 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="max-w-3xl text-white"
          >
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-white/70 mb-6">
              <span className="w-8 h-px bg-[#E10600]" />
              PU Designböden
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl leading-[1.05] font-light tracking-tight">
              Fugenlose <span className="italic font-medium text-[#E10600]">Hochglanz-</span>böden mit Charakter.
            </h1>
            <p className="mt-8 text-base sm:text-lg text-white/85 max-w-2xl leading-relaxed">
              Flüssig verlegte Polyurethan-Beschichtungen – marmoriert,
              uni oder metallisch. Spiegelglatte Oberflächen für Wohnen,
              Gewerbe und Praxis.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                data-testid="pu-hero-cta"
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#1C1C1C] text-sm tracking-wide hover:bg-[#E10600] hover:text-white transition-colors duration-300"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#E10600] group-hover:bg-white transition-colors" />
                Kostenlose Beratung anfragen
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="space-y-8">
            <div className="text-xs uppercase tracking-[0.3em] text-stone-500">
              Was ist ein PU Designboden?
            </div>
            <p className="font-heading text-2xl sm:text-3xl lg:text-4xl leading-[1.25] text-[#1C1C1C] font-light">
              Ein PU Designboden ist eine flüssig verlegte Polyurethan-Beschichtung,
              die fugenlos, hoch belastbar und in nahezu jeder Optik gestaltet
              werden kann. Das Ergebnis: ein moderner Boden mit Charakter –
              vom strahlend weißen Spiegel-Look bis zur dramatischen Marmorierung.
            </p>
          </motion.div>
        </div>
      </section>

      {/* GALLERY (3 real photos) */}
      <section data-testid="pu-gallery-section" className="py-16 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-12">
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">
              <span className="w-8 h-px bg-[#E10600]" />
              Referenzen
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              Projekte in <span className="text-[#E10600] italic font-medium">Hochglanz</span>.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pu-gallery-grid">
            {[
              { img: IMG.pu.white,  title: "Reinweiß Hochglanz", sub: "Wohnraum mit Panoramablick" },
              { img: IMG.pu.hero,   title: "Carrara Marmor PU",  sub: "Wintergarten / Atelier" },
              { img: IMG.pu.detail, title: "Marmor-Detail",      sub: "Handgezogene Marmorierung" },
            ].map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="group"
                data-testid={`pu-gallery-item-${i}`}
              >
                <div className="aspect-[4/5] overflow-hidden bg-stone-100">
                  <img
                    src={p.img}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="mt-4">
                  <div className="font-heading text-lg font-medium">{p.title}</div>
                  <div className="text-xs text-stone-500 uppercase tracking-[0.2em] mt-1">{p.sub}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section data-testid="pu-benefits-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-3xl mb-16">
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">
              <span className="w-8 h-px bg-[#E10600]" />
              Vorteile
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              Warum PU Designboden.
            </h2>
          </motion.div>

          <div
            data-testid="pu-benefits-grid"
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12"
          >
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`pu-benefit-${i}`}
              >
                <b.icon size={28} strokeWidth={1.4} className="text-[#1C1C1C] mb-5" />
                <h3 className="font-heading text-lg font-medium mb-2">{b.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{b.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATIONS */}
      <section className="py-24 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp} className="relative aspect-[4/5] overflow-hidden">
            <img
              src={IMG.pu.detail}
              alt="PU Designboden Detail"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
          <motion.div {...fadeUp} className="space-y-8">
            <div className="text-xs uppercase tracking-[0.3em] text-stone-500">
              Einsatzbereiche
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              Wo PU Böden<br />zur Geltung kommen.
            </h2>
            <ul className="space-y-4 pt-2" data-testid="pu-applications-list">
              {applications.map((t, i) => (
                <li key={i} className="flex gap-4 items-start" data-testid={`pu-app-${i}`}>
                  <Check size={20} strokeWidth={1.8} className="mt-0.5 shrink-0 text-[#E10600]" />
                  <span className="text-[15px] text-stone-700 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-[#1C1C1C] text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-white/60 mb-6">
            <span className="w-8 h-px bg-[#E10600]" />
            Beratung
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
            Bereit für einen Boden,<br />
            der <span className="text-[#E10600] italic font-medium">glänzt</span>?
          </h2>
          <p className="mt-8 text-white/75 max-w-xl mx-auto">
            Wir beraten Sie kostenlos vor Ort, zeigen Musterflächen und
            erstellen ein transparentes Angebot für Ihr Projekt.
          </p>
          <Link
            to="/kontakt"
            data-testid="pu-cta-button"
            className="mt-12 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-[#1C1C1C] text-sm hover:bg-[#E10600] hover:text-white transition-colors"
          >
            Jetzt Beratung anfragen <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
